// src/App.jsx
import { Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Wallet,
  LayoutDashboard,
  LogOut,
  X,
  Menu,
  Shield,
  Users,
  ClipboardCheck,
  History,
  Clock,
  Home,
  TrendingUp,
  MessageSquare,
  User,
  Settings,
  BarChart3,
  HelpCircle,
  Activity,
  // ✅ Admin icon
  ShieldCheck,
} from "lucide-react";
import { useEffect, useState } from "react";

import { RequireAuth } from "@app/routes/RequireAuth";
import { RequireRole } from "@app/routes/RequireRole";

import AuthLayout from "@app/layouts/AuthLayout";
import AppLayout from "@app/layouts/AppLayout";

import LoginPage from "@app/pages/auth/LoginPage";
import RegisterPage from "@app/pages/auth/RegisterPage";
import KycVerificationPage from "@app/pages/auth/KycVerificationPage";
import KycPendingPage from "@app/pages/auth/KycPendingPage";
import ForgotPasswordPage from "@app/pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "@app/pages/auth/ResetPasswordPage";

import TradePage from "@app/pages/app/TradePage";
import RequestsPage from "@app/pages/app/RequestsPage";
import ModerationPage from "@app/pages/app/ModerationPage";
import WalletPage from "@app/pages/app/WalletPage";
import MarketsPage from "@app/pages/app/MarketsPage";
import DashboardPage from "@app/pages/app/DashboardPage";
import AccountPage from "@app/pages/app/AccountPage";
import StatisticsPage from "@app/pages/app/StatisticsPage";
import SettingsPage from "@app/pages/app/SettingsPage";
import ChatPage from "@app/pages/app/ChatPage";
import HelpLoanPage from "@app/pages/app/HelpLoanPage";
import LeveragePage from "@app/pages/app/LeveragePage";
import TradeHistoryPage from "@app/pages/app/TradeHistoryPage";

import { AdminDashboard } from "@app/pages/admin/AdminDashboard";
import TreasuryDashboard from "@app/pages/admin/TreasuryDashboard";

import { AdminUserBalancesPage } from "@app/pages/admin/AdminUserBalancesPage";
import AdminTradeRequestsPage from "@app/pages/admin/AdminTradeRequestsPage";
import AdminTradeManagement from "@app/pages/admin/AdminTradeManagement";
import AdminTradeHistoryPage from "@app/pages/admin/AdminTradeHistoryPage";
// ✅ Import AdminChatPage
import AdminChatPage from "@app/pages/admin/AdminChatPage";

import { MarketProvider } from "@app/features/markets/marketContext";
import { useAuthSession } from "@app/features/auth/useAuth";
import { clearSession } from "@app/state/authSession";

const queryClient = new QueryClient();

// ✅ Main app navigation item style
const navItem = ({ isActive }) =>
  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300
   ${
     isActive
       ? "bg-gradient-to-r from-indigo-600/20 to-indigo-500/20 text-indigo-300 border border-indigo-500/30 shadow-lg shadow-indigo-500/10"
       : "text-gray-400 hover:text-white hover:bg-gray-800/50 border border-transparent hover:border-gray-700"
   }`;

// Admin navigation item style
const adminNavItem = ({ isActive }) =>
  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300
   ${
     isActive
       ? "bg-gradient-to-r from-amber-600/20 to-amber-500/20 text-amber-300 border border-amber-500/30 shadow-lg shadow-amber-500/10"
       : "text-gray-400 hover:text-white hover:bg-gray-800/50 border border-transparent hover:border-gray-700"
   }`;

function getAdminRouteMeta(pathname) {
  if (pathname === "/admin" || pathname === "/admin/") {
    return {
      title: "Admin Dashboard",
      subtitle: "System Overview",
      icon: LayoutDashboard,
    };
  }
  if (pathname.startsWith("/admin/treasury")) {
    return {
      title: "Treasury Management",
      subtitle: "Wallet & Balance Control",
      icon: Wallet,
    };
  }
  if (pathname.startsWith("/admin/users/balances")) {
    return {
      title: "User Balances",
      subtitle: "Search & Adjust Ledger",
      icon: Users,
    };
  }
  if (pathname.startsWith("/admin/trade-requests")) {
    return {
      title: "Trade Requests",
      subtitle: "Approve or Reject Trades",
      icon: ClipboardCheck,
    };
  }
  if (pathname.startsWith("/admin/trade-management")) {
    return {
      title: "Trade Management",
      subtitle: "Set WIN/LOSE/PUSH Outcomes",
      icon: Clock,
    };
  }
  if (pathname.startsWith("/admin/trade-history")) {
    return {
      title: "Trade History",
      subtitle: "Completed Trades (Admin)",
      icon: History,
    };
  }
  // ✅ Add chat route meta
  if (pathname.startsWith("/admin/chat")) {
    return {
      title: "Chat Support",
      subtitle: "Customer Conversations",
      icon: MessageSquare,
    };
  }

  return { title: "Admin", subtitle: "System Management", icon: Shield };
}

// ✅ Admin Layout
function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, session } = useAuthSession();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const meta = getAdminRouteMeta(location.pathname);

  const handleLogout = () => {
    clearSession();
    navigate("/login");
  };

  const displayName =
    user?.name ||
    user?.username ||
    user?.email?.split?.("@")?.[0] ||
    session?.user?.email?.split?.("@")?.[0] ||
    "Admin";

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <aside
        className={`
          fixed left-0 top-0 h-screen w-72 border-r border-gray-800/50
          bg-gradient-to-b from-gray-900 to-gray-950 p-4 sm:p-6 z-50 shadow-2xl
          overflow-y-auto transition-transform duration-300 ease-in-out
          ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"}
          lg:translate-x-0 lg:w-64
        `}
      >
        <div className="flex items-center justify-between pb-6 mb-6 border-b border-gray-800 sticky top-0 bg-gradient-to-b from-gray-900 to-gray-950 z-10">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl bg-gradient-to-br from-amber-600 to-amber-500 grid place-items-center font-bold text-white shadow-lg">
              <Shield size={20} className="sm:w-6 sm:h-6" />
            </div>
            <div>
              <div className="text-base sm:text-lg font-bold bg-gradient-to-r from-amber-400 to-amber-300 bg-clip-text text-transparent">
                Admin Panel
              </div>
              <div className="text-xs text-gray-400 hidden sm:block">
                System Management
              </div>
            </div>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-800/50 transition-colors"
            type="button"
          >
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        <div className="px-4 pt-2 pb-2 text-[11px] tracking-wider uppercase text-gray-500">
          <span>Admin Controls</span>
        </div>

        <nav className="space-y-2 px-1 pb-4">
          <NavLink to="/admin" end className={adminNavItem}>
            <LayoutDashboard size={20} className="flex-shrink-0" />
            <span>Dashboard</span>
          </NavLink>

          <NavLink to="/admin/treasury" className={adminNavItem}>
            <Wallet size={20} className="flex-shrink-0" />
            <span>Treasury Management</span>
          </NavLink>

          <NavLink to="/admin/users/balances" className={adminNavItem}>
            <Users size={20} className="flex-shrink-0" />
            <span>User Balances</span>
          </NavLink>

          <NavLink to="/admin/trade-requests" className={adminNavItem}>
            <ClipboardCheck size={20} className="flex-shrink-0" />
            <span>Trade Requests</span>
          </NavLink>

          <NavLink to="/admin/trade-management" className={adminNavItem}>
            <Clock size={20} className="flex-shrink-0" />
            <span>Trade Management</span>
          </NavLink>

          <NavLink to="/admin/trade-history" className={adminNavItem}>
            <History size={20} className="flex-shrink-0" />
            <span>Trade History</span>
          </NavLink>

          {/* ✅ Add Chat Support link */}
          <NavLink to="/admin/chat" className={adminNavItem}>
            <MessageSquare size={20} className="flex-shrink-0" />
            <span>Chat Support</span>
            {/* Optional: Add a badge for unread messages */}
            <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">
              Live
            </span>
          </NavLink>
        </nav>

        {/* ✅ Back to Main App Link */}
        <div className="px-4 pt-4 pb-2 border-t border-gray-800/50">
          <NavLink to="/app/trade" className={adminNavItem}>
            <Home size={20} className="flex-shrink-0" />
            <span>Back to App</span>
          </NavLink>
        </div>

        <div className="h-6" />

        <div className="sticky bottom-6 bg-gradient-to-b from-gray-900/95 to-gray-950/95 backdrop-blur-sm pt-2">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-700 hover:to-gray-800 text-gray-300 hover:text-white border border-gray-700 hover:border-gray-600 transition-all duration-300 group"
            type="button"
          >
            <LogOut
              size={18}
              className="group-hover:rotate-12 transition-transform"
            />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </aside>

      <main className="min-h-screen lg:ml-64">
        <div className="sticky top-0 z-40 h-14 sm:h-16 border-b border-gray-800/50 bg-gray-900/95 backdrop-blur-xl flex items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-gray-800/50 transition-colors"
              type="button"
            >
              <Menu size={20} className="text-gray-400" />
            </button>

            <div className="flex items-center gap-2 min-w-0">
              <meta.icon className="w-5 h-5 text-amber-400 flex-shrink-0" />
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-sm sm:text-base font-semibold text-white truncate">
                  {meta.title}
                </span>
                <span className="hidden sm:inline text-gray-500 mx-2">/</span>
                <span className="hidden sm:inline text-xs sm:text-sm text-gray-400 truncate">
                  {meta.subtitle}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <div className="flex items-center gap-2 sm:gap-3 pl-2 sm:pl-3 py-1.5 sm:py-2">
              <div className="text-right">
                <div className="text-sm font-semibold text-white">{displayName}</div>
                <div className="text-xs text-amber-400">Administrator</div>
              </div>
              <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center text-white text-sm sm:text-base font-bold shadow-lg">
                {String(displayName).slice(0, 2).toUpperCase()}
              </div>
            </div>
          </div>
        </div>

        <div className="p-3 sm:p-4 md:p-6 lg:p-8 animate-fade-in">
          <div className="bg-gradient-to-br from-gray-900 to-gray-950 rounded-xl sm:rounded-2xl border border-gray-800 shadow-2xl overflow-hidden">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
}

export default function App() {
  const isDev = import.meta.env.DEV;

  return (
    <QueryClientProvider client={queryClient}>
      <MarketProvider>
        <Routes>
          <Route path="/" element={<Navigate to="/app/trade" replace />} />
          <Route path="/trade" element={<Navigate to="/app/trade" replace />} />

          <Route path="/auth" element={<AuthLayout />}>
            <Route path="login" element={<LoginPage />} />
            <Route path="register" element={<RegisterPage />} />
            <Route path="kyc-verification" element={<KycVerificationPage />} />
            <Route path="kyc-pending" element={<KycPendingPage />} />
            <Route path="forgot-password" element={<ForgotPasswordPage />} />
            <Route path="reset-password" element={<ResetPasswordPage />} />
          </Route>

          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />

          <Route
            path="/app/*"
            element={
              <RequireAuth>
                <AppLayout />
              </RequireAuth>
            }
          >
            <Route index element={<Navigate to="trade" replace />} />
            <Route path="trade" element={<TradePage />} />
            <Route path="requests" element={<RequestsPage />} />
            <Route path="account" element={<AccountPage />} />
            <Route path="statistics" element={<StatisticsPage />} />
            <Route path="trade-history" element={<TradeHistoryPage />} />
            <Route path="chat" element={<ChatPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="wallet" element={<WalletPage />} />
            <Route path="markets" element={<MarketsPage />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="help-loan" element={<HelpLoanPage />} />
            <Route path="leverage" element={<LeveragePage />} />
            <Route
              path="moderation"
              element={
                <RequireRole allow={["ADMIN", "MODERATOR"]}>
                  <ModerationPage />
                </RequireRole>
              }
            />
          </Route>

          <Route
            path="/admin/*"
            element={
              <RequireAuth requireKyc={false}>
                <RequireRole allow={["ADMIN"]}>
                  <AdminLayout />
                </RequireRole>
              </RequireAuth>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="treasury" element={<TreasuryDashboard />} />
            <Route path="users/balances" element={<AdminUserBalancesPage />} />
            <Route path="trade-requests" element={<AdminTradeRequestsPage />} />
            <Route path="trade-management" element={<AdminTradeManagement />} />
            <Route path="trade-history" element={<AdminTradeHistoryPage />} />
            {/* ✅ Add Chat Support route */}
            <Route path="chat" element={<AdminChatPage />} />
          </Route>

          <Route
            path="*"
            element={<div className="p-10 text-center">404 - Not Found</div>}
          />
        </Routes>

        {isDev ? <ReactQueryDevtools initialIsOpen={false} /> : null}
      </MarketProvider>
    </QueryClientProvider>
  );
}
